import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { Check, ChevronsUpDown, Cookie, LogOut, User } from "lucide-react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarMenuButton } from "@/components/ui/sidebar"
import { consultarGrupos, logout } from "@/lib/auth"
import { useConsentimentoStore } from "@/stores/consentimentoStore"
import { useSessionStore, type GrupoInfo } from "@/stores/sessionStore"
export function AccountSwitcher() {
  const navigate = useNavigate()
  const username = useSessionStore((s) => s.username)
  const fullName = useSessionStore((s) => s.fullName)
  const email = useSessionStore((s) => s.email)
  const roles = useSessionStore((s) => s.roles)
  const grupos = useSessionStore((s) => s.groups)
  const grupoAtivo = useSessionStore((s) => s.grupoAtivo)
  const setGrupoAtivo = useSessionStore((s) => s.setGrupoAtivo)
  const clear = useSessionStore((s) => s.clear)
  const abrirPreferencias = useConsentimentoStore((s) => s.abrirPreferencias)
  const queryClient = useQueryClient()

  const nome = fullName?.trim() || username || "Usuário"
  const partes = nome.split(/\s+/).filter(Boolean)
  const iniciais = (
    partes.length >= 2
      ? `${partes[0].slice(0, 1)}${partes[partes.length - 1].slice(0, 1)}`
      : (partes[0] ?? "?").slice(0, 2)
  ).toUpperCase()

  // Admin global (espelha isGlobalScope no backend) pode atuar em qualquer
  // grupo ou em "Todos os grupos" (sem header X-Grupo).
  const ehAdminGlobal = roles.includes("SIGI_ADMIN")
  const escopo =
    grupoAtivo?.nome ?? (ehAdminGlobal ? "Todos os grupos" : "Sem grupo ativo")

  const { data: catalogo } = useQuery({
    queryKey: ["grupos-catalogo"],
    queryFn: consultarGrupos,
    staleTime: 5 * 60_000,
    enabled: ehAdminGlobal,
  })
  const opcoes: GrupoInfo[] = ehAdminGlobal ? (catalogo?.length ? catalogo : grupos) : grupos

  const trocarGrupo = (grupo: GrupoInfo | null) => {
    setGrupoAtivo(grupo)
    // O escopo de dados mudou: recarrega tudo.
    void queryClient.invalidateQueries()
  }

  const sair = async () => {
    clear()
    // logout() revoga no backend e redireciona para o encerramento da SSO
    // no Keycloak (RP-initiated logout), voltando em /login.
    await logout()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          aria-label="Perfil"
          className="data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground"
        >
          <Avatar className="size-8">
            <AvatarFallback className="bg-sidebar-accent text-xs font-semibold text-sidebar-accent-foreground">
              {iniciais}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate text-sm font-semibold">{nome}</span>
            <span className="truncate text-xs text-sidebar-foreground/70">{escopo}</span>
          </div>
          <ChevronsUpDown className="ml-auto size-4 shrink-0 text-sidebar-foreground/70 group-data-[collapsible=icon]:hidden" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate text-sm">{nome}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">
            {email ?? username}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Atuando como
        </DropdownMenuLabel>
        {opcoes.length === 0 && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            Nenhum grupo atribuído ao usuário.
          </div>
        )}
        {opcoes.map((g) => (
          <DropdownMenuItem
            key={g.id}
            onClick={() => trocarGrupo(g)}
            className="justify-between gap-2"
          >
            <span className="truncate">{g.nome}</span>
            {grupoAtivo?.nome === g.nome && <Check className="size-4 shrink-0" />}
          </DropdownMenuItem>
        ))}
        {ehAdminGlobal && (
          <DropdownMenuItem onClick={() => trocarGrupo(null)} className="justify-between gap-2">
            <span>Todos os grupos</span>
            {grupoAtivo === null && <Check className="size-4 shrink-0" />}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/perfil")}>
          <User />
          Meu perfil
        </DropdownMenuItem>
        <DropdownMenuItem onClick={abrirPreferencias}>
          <Cookie />
          Preferências de cookies
        </DropdownMenuItem>
        <DropdownMenuItem onClick={sair}>
          <LogOut />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
